import Box from "@mui/material/Box"
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";
import { toggleAskActivation } from "../../utils/apis/memeverse";
import Switch from "@mui/material/Switch";
import { USER_TYPE } from "../../config/const";

const enableAskUsers = process.env.REACT_APP_ENABLE_ASK_USERS?.toLowerCase() === 'true';

const ParticipationSettings: React.FC = () => {
  const theme = useTheme();
  const { authUser, updateVerifiedStatus } = useAuth();
  const [isPublished, setIsPublished] = useState<boolean>(authUser?.verified ?? false);
  const [isOnAsk, setIsOnAsk] = useState<boolean>(authUser?.qdrant_data?.is_active ?? false);

  useEffect(() => {
    if (authUser?.documentId) {
      // Update verified status when authUser changes
      setIsPublished(authUser?.verified ?? false);
      setIsOnAsk(authUser?.qdrant_data?.is_active ?? false);
    }
  }, [authUser]);

  const handleTogglePublish = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStatus = event.target.checked;
    setIsPublished(newStatus);

    // Update backend or show a toast message on failure
    try {
      if (authUser?.documentId) {
        await updateVerifiedStatus(authUser.documentId, newStatus);
        toast.success(
          `Persona ${newStatus ? 'published' : 'unpublished'} successfully`
        );
      }
    } catch (error) {
      toast.error('Failed to update persona status. Please try again.');
      setIsPublished(!newStatus); // Revert on failure
    }
  };

  const handleToggleAskParticipation = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStatus = event.target.checked;
    setIsOnAsk(newStatus);

    // Update backend or show a toast message on failure
    try {
      if (authUser?.documentId) {
        let response = await toggleAskActivation(authUser.documentId);
        if (!response) {
          toast.error('Failed to toggle persona status. Please try again.');
          setIsOnAsk(!newStatus);
          return;
        }
        let data = await response.json();
        if (data?.success) {
          toast.success(
            `Persona ${newStatus ? 'added to Ask' : 'removed from Ask'} successfully`
          );
        } else {
          toast.error('Failed to toggle persona status. Please try again.');
          setIsOnAsk(!newStatus)
        }
      } else {
        setIsOnAsk(!newStatus)
      }
    } catch (error) {
      toast.error('Failed to toggle persona status. Please try again.');
      setIsOnAsk(!newStatus); // Revert on failure
    }
  };

  if (!(authUser?.userType === USER_TYPE.CREATOR)) {
    // Skip component if authUser not a creator
    return null
  }

  return (
    <Box
      sx={{
        py: 3,
        display: "flex",
        flexDirection: 'column',
        gap: 2,
        width: '100%',
      }}
    >
      <Box>
          <Typography variant="h6">AI Persona Participation</Typography>
      </Box>

      <Box
        sx={{
          paddingY: 1,
          border: `2px solid ${theme.palette.divider}`,
          borderRadius: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Box
          sx={{
            paddingX: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Switch
            id='toggle-publish-persona'
            checked={isPublished}
            onChange={handleTogglePublish}
            size="small"
            sx={{
              px: 0,
            }}
          />
          <Box sx={{
              display: 'flex',
              flexDirection: 'column',
          }}>
              <Typography variant="body1">
                <strong>Publish AI Persona</strong>
              </Typography>
              <Typography variant="body2">
                Your AI persona is public and available to chat with all Soopra users
              </Typography>
          </Box>
        </Box>
      </Box>

      {
        enableAskUsers &&
        <Box
          sx={{
            paddingY: 1,
            border: `2px solid ${theme.palette.divider}`,
            borderRadius: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <Box
            sx={{
              paddingX: 2,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Switch
              id='toggle-ask-participation'
              checked={isOnAsk}
              onChange={handleToggleAskParticipation}
              size="small"
              sx={{
                px: 0,
              }}
            />
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>
                <Typography variant="body1">
                  <strong>Answer questions in Soopra Ask</strong>
                </Typography>
                <Typography variant="body2">
                  Your AI persona can reply to questions in Soopra Ask
                </Typography>
            </Box>
          </Box>
        </Box>
      }
    </Box>
  )
}

export default ParticipationSettings;
